import "@/App.css";
import "./i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminProvider, useAdmin } from "./context/AdminContext";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import SharedReportPage from "./pages/SharedReportPage";
import GuestAnalyzePage from "./pages/GuestAnalyzePage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import ReportPage from "./pages/ReportPage";
import TrendsPage from "./pages/TrendsPage";
import ChatPage from "./pages/ChatPage";
import ProfilePage from "./pages/ProfilePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import PaymentResultPage from "./pages/PaymentResultPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminPlansPage from "./pages/admin/AdminPlansPage";
import AdminCouponsPage from "./pages/admin/AdminCouponsPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminPaymentsPage from "./pages/admin/AdminPaymentsPage";
import AdminTransactionsPage from "./pages/admin/AdminTransactionsPage";
import AdminAISettingsPage from "./pages/admin/AdminAISettingsPage";
import AdminLayout from "./components/admin/AdminLayout";
import { Loader2 } from "lucide-react";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();
    
    // If user data was passed from AuthCallback, skip loading
    if (location.state?.user) {
        return children;
    }
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto mb-4" />
                    <p className="text-stone-500">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <Loader2 className="w-10 h-10 animate-spin text-teal-500" />
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

// Layout with Navbar for authenticated pages
const AuthenticatedLayout = ({ children }) => {
    return (
        <div className="min-h-screen bg-stone-50">
            <Navbar />
            <main>{children}</main>
        </div>
    );
};

// Layout for landing page (with navbar but different style)
const PublicLayout = ({ children }) => {
    return (
        <div className="min-h-screen bg-stone-50">
            <Navbar />
            <main>{children}</main>
        </div>
    );
};

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAdmin();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <Loader2 className="w-10 h-10 animate-spin text-teal-500" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/admin/login" replace />;
    }
    
    return children;
};

// Router component that handles session_id detection
function AppRouter() {
    const location = useLocation();
    
    // Check URL fragment for session_id synchronously during render
    // This prevents race conditions by processing new session_id FIRST
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={
                <PublicLayout>
                    <LandingPage />
                </PublicLayout>
            } />
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            <Route path="/register" element={
                <PublicRoute>
                    <RegisterPage />
                </PublicRoute>
            } />
            
            {/* Auth Callback Route */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Password Reset Routes */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Email Verification Route */}
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />

            {/* Public shared report (no auth) */}
            <Route path="/shared/:token" element={<SharedReportPage />} />

            {/* Guest analysis (no auth) */}
            <Route path="/try" element={<GuestAnalyzePage />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <DashboardPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/upload" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <UploadPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/report/:reportId" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <ReportPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/trends" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <TrendsPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/trends/:testName" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <TrendsPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/chat" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <ChatPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/profile" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <ProfilePage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/subscription" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <SubscriptionPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />
            <Route path="/payment/result" element={
                <ProtectedRoute>
                    <AuthenticatedLayout>
                        <PaymentResultPage />
                    </AuthenticatedLayout>
                </ProtectedRoute>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// Admin Routes Component (wrapped with AdminProvider)
function AdminRoutes() {
    return (
        <AdminProvider>
            <Routes>
                <Route path="login" element={<AdminLoginPage />} />
                <Route path="/" element={
                    <AdminProtectedRoute>
                        <AdminLayout />
                    </AdminProtectedRoute>
                }>
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboardPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="plans" element={<AdminPlansPage />} />
                    <Route path="coupons" element={<AdminCouponsPage />} />
                    <Route path="payments" element={<AdminPaymentsPage />} />
                    <Route path="transactions" element={<AdminTransactionsPage />} />
                    <Route path="reports" element={<AdminReportsPage />} />
                    <Route path="ai-settings" element={<AdminAISettingsPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                </Route>
            </Routes>
        </AdminProvider>
    );
}

function App() {
    return (
        <BrowserRouter>
            <Routes>
                {/* Admin Routes - separate provider */}
                <Route path="/admin/*" element={<AdminRoutes />} />
                
                {/* Main App Routes */}
                <Route path="/*" element={
                    <AuthProvider>
                        <AppRouter />
                    </AuthProvider>
                } />
            </Routes>
            <Toaster 
                position="top-right"
                toastOptions={{
                    style: {
                        background: 'white',
                        border: '1px solid #E7E5E4',
                        borderRadius: '12px',
                    },
                }}
            />
        </BrowserRouter>
    );
}

export default App;
